import React, { useMemo } from "react";
import {
  Typography,
  IconButton,
  TextField,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Stack,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Box,
  ListItemIcon,
  Button,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Person as PersonIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Search as SearchIcon,
  ChevronLeft as ChevronLeftIcon,
  List as ListIcon,
  DoneAll as DoneAllIcon,
  Error as ErrorIcon,
  Circle as CircleIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import {
  ServiceStatusAggregation,
  MonitorGroup,
  FavoriteService,
} from "../../Models/Types";
import "./MonitorList.scss";
import { MAX_SHOW_ITEMS } from "../../Pages/DashboardPage/DashboardPage";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import KSPMonitorView from "../KSPMonitorView/KSPMonitorView";

// Helpers
const formatDuration = (seconds: number | string): string => {
  const secNum = typeof seconds === "string" ? parseFloat(seconds) : seconds;
  if (isNaN(secNum)) return "00:00:00";
  const h = Math.floor(secNum / 3600);
  const m = Math.floor((secNum % 3600) / 60);
  return `${h.toString()}h ${m.toString()}m`;
};

const formatDate = (isoString: string) => {
  if (!isoString) return "-";
  return new Date(isoString).toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

interface MonitorListProps {
  aggregations: ServiceStatusAggregation[];
  allResultsBeforeStatusFilter: ServiceStatusAggregation[];
  favorites: FavoriteService[];
  loading: boolean;
  activeTab: number;
  filteringGroup: MonitorGroup | null;
  searchText: string;
  setSearchText: (val: string) => void;
  sortOrder: "last_exec" | "alpha";
  setSortOrder: (val: "last_exec" | "alpha") => void;
  filterByRunState: "any" | "running" | "stopped" | "paused";
  setFilterByRunState: (val: "any" | "running" | "stopped" | "paused") => void;
  onToggleFavorite: (appId: number, e: React.MouseEvent) => void;
  onBackToMain: () => void;
}

const MonitorList: React.FC<MonitorListProps> = ({
  aggregations,
  allResultsBeforeStatusFilter,
  favorites,
  loading,
  activeTab,
  filteringGroup,
  searchText,
  setSearchText,
  sortOrder,
  setSortOrder,
  filterByRunState,
  setFilterByRunState,
  onToggleFavorite,
  onBackToMain,
}) => {
  const counts = useMemo(() => {
    const source = allResultsBeforeStatusFilter || [];
    return {
      total: source.length,
      running: source.filter((a) => a.aggregate_status).length,
      error: source.filter((a) => !a.aggregate_status).length,
    };
  }, [allResultsBeforeStatusFilter]);

  const getListTitle = () => {
    if (filteringGroup) {
      return (
        <Typography className="section-title">
          <IconButton onClick={onBackToMain} color="primary">
            <ChevronLeftIcon />
          </IconButton>
          {filteringGroup.groupName}
        </Typography>
      );
    }
    if (activeTab === 0)
      return <Typography className="section-title"></Typography>;
    if (activeTab === 1)
      return <Typography className="section-title">Preferiti</Typography>;
    if (activeTab === 2)
      return <Typography className="section-title">KSP</Typography>;
    return <div />;
  };

  if (loading) {
    return (
      <div className="section-container bg-gray">
        <Typography className="section-title"></Typography>
        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            marginTop: "4em",
          }}
        >
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (activeTab === 2 && !filteringGroup) {
    return (
      <div className="section-container bg-gray">
        {/* Pass all results without standard status filters, 
            because the KSP component handles its own filters internally */}
        <KSPMonitorView aggregations={allResultsBeforeStatusFilter} />
      </div>
    );
  }

  const getAppDescription = (desc: string) => {
    if (!desc || desc.trim() === "") {
      return "Nessuna descrizione disponibile.";
    }
    return desc;
  };

  return (
    <div className="section-container bg-gray">
      {getListTitle()}

      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 2, mt: 1, overflowX: "auto", pb: 0.5 }}
      >
        <Chip
          icon={<ListIcon />}
          label={`${counts.total}`}
          clickable
          color="primary"
          variant={filterByRunState === "any" ? "filled" : "outlined"}
          onClick={() => setFilterByRunState("any")}
        />
        <Chip
          icon={<DoneAllIcon />}
          label={`${counts.running}`}
          clickable
          color="success"
          variant={filterByRunState === "running" ? "filled" : "outlined"}
          onClick={() => setFilterByRunState("running")}
        />
        <Chip
          icon={<ErrorIcon />}
          label={`${counts.error}`}
          clickable
          color="error"
          variant={filterByRunState === "stopped" ? "filled" : "outlined"}
          onClick={() => setFilterByRunState("stopped")}
        />
      </Stack>

      {activeTab === 0 && !filteringGroup && (
        <div className="monitor-filters">
          <TextField
            placeholder="Prodotto o referente"
            variant="filled"
            size="small"
            className="search-input"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon color="action" sx={{ mt: "0.5em", mr: 1 }} />
              ),
            }}
            sx={{
              "& .MuiFilledInput-root": { backgroundColor: "transparent" },
            }}
          />
          <FormControl size="small" variant="filled" sx={{ minWidth: 110 }}>
            <InputLabel id="sort-label">Ordinamento</InputLabel>
            <Select
              labelId="sort-label"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as any)}
              sx={{ backgroundColor: "transparent" }}
            >
              <MenuItem value="last_exec">Ultima es.</MenuItem>
              <MenuItem value="alpha">Alfabetico</MenuItem>
            </Select>
          </FormControl>
        </div>
      )}

      <div style={{ marginTop: "1em" }}>
        {aggregations.map((agg) => (
          <Accordion key={agg.application_id} className="monitor-accordion">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <div className="summary-content">
                <div
                  className="fav-icon"
                  onClick={(e) => onToggleFavorite(agg.application_id, e)}
                >
                  {favorites.some(
                    (x) => x.applicationId === agg.application_id,
                  ) ? (
                    <StarIcon sx={{ color: "#ffb400" }} />
                  ) : (
                    <StarBorderIcon sx={{ color: "#bdbdbd" }} />
                  )}
                </div>
                <div className="info-box">
                  <span className="service-name">
                    {agg.is_kpi ? (
                      <BookmarkIcon sx={{ color: "#ffb400" }} />
                    ) : (
                      <BookmarkBorderIcon sx={{ color: "#bdbdbd" }} />
                    )}
                    {agg.application}
                  </span>

                  <span className="service-meta">
                    <PersonIcon
                      className="time-icon"
                      sx={{ fontSize: "1.2rem" }}
                    />
                    <strong>Referente:</strong>
                    <span>{agg.items[0].responsible || "N/D"}</span>
                  </span>

                  <span className="service-meta">
                    <div
                      className="service-duration-color-cicle"
                      style={{
                        backgroundColor: agg.aggregate_status ? "green" : "red",
                      }}
                    ></div>
                    <strong>Status:</strong>
                    {agg.koMonitors === 0
                      ? "OK"
                      : `${agg.koMonitors} KO su ${agg.items.length}`}
                  </span>
                  <span className="service-meta">
                    <AccessTimeIcon className="time-icon" />
                    <strong>Durata stato:</strong>
                    <span>
                      {agg.aggregate_status
                        ? formatDuration(agg.minDuration)
                        : formatDuration(agg.maxDuration)}
                    </span>
                  </span>
                </div>
              </div>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                bgcolor: "#f9fafb",
                borderTop: "1px solid #eee",
                padding: 0,
              }}
            >
              <div style={{ padding: "16px" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                  }}
                >
                  <Typography sx={{ mt: 1, mb: 1, fontWeight: "bold" }}>
                    Descrizione:
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<OpenInNewIcon />}
                    onClick={() => {
                      const link = `https://www.symon.it/dashboard/applications/${agg.application_id}/summary?timeframe=7200`;
                      console.log("Navigating to: " + link);
                      window.open(link, "_blank", "noopener,noreferrer");
                    }}
                    sx={{ textTransform: "none" }}
                  >
                    Vai al dettaglio
                  </Button>
                </Box>

                <Typography variant="body2" color="textSecondary" paragraph>
                  {getAppDescription(agg.application_description)}
                </Typography>

                <Typography sx={{ mt: 1, mb: 1, fontWeight: "bold" }}>
                  Monitors:
                </Typography>
                <List dense disablePadding>
                  {[...agg.items]
                    .sort((a, b) => {
                      // If statuses are the same, don't change order
                      if (a.run_status === b.run_status) return 0;
                      // If a is OK (true) and b is KO (false), put a after b (return 1)
                      // If a is KO (false) and b is OK (true), put a before b (return -1)
                      return a.run_status ? 1 : -1;
                    })
                    .map((item) => (
                      <ListItem
                        key={item.monitor_service_id}
                        divider
                        sx={{ pl: 0 }}
                      >
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CircleIcon
                            sx={{
                              fontSize: 12,
                              color: item.run_status ? "green" : "red",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText>
                          <Typography variant="body1" component="span">
                            {item.monitor_name}
                          </Typography>
                          <br />
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            component="span"
                          >
                            Durata stato: {formatDuration(item.duration_secs)}
                          </Typography>
                        </ListItemText>
                      </ListItem>
                    ))}
                </List>
              </div>
            </AccordionDetails>
          </Accordion>
        ))}
        {aggregations.length === 0 && !loading && (
          <Typography align="center" color="text.secondary" sx={{ mt: 2 }}>
            Nessun servizio trovato.
          </Typography>
        )}
      </div>
    </div>
  );
};

export default MonitorList;
